import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, InlineNotification, Link, Loading } from "@carbon/react";
import axios from "../../axios-orders";
import { resetPasswordValidationSchema } from "../../utils";
import AuthLayout from "../carbon/AuthLayout/AuthLayout";
import CarbonForm from "../carbon/Form/Form";
import TextField from "../carbon/Form/TextField";
import "./resetPassword.scss";

type ResetPasswordFormValues = {
  email: string;
};

/**
 * The reset-password request screen (`POST /auth/reset-password`), rebuilt on the Carbon
 * primitives (Task 6). Task 2 made the response opaque — a bare 204 No Content, no reset token
 * echoed back in the body — but this screen never read the response body before and still
 * doesn't: only the mutation's own `isSuccess`/`isError` status (2xx vs not) drives the
 * confirmation banner, exactly as before. The form itself stays visible after a successful
 * submit (it did in the pre-Carbon version too — the confirmation banner is additive, not a
 * screen swap), so the user can immediately request another reset email if the first didn't
 * arrive.
 */
const ResetPassword = () => {
  const { t } = useTranslation();
  const { control, handleSubmit, reset } = useForm<ResetPasswordFormValues>({
    resolver: yupResolver(resetPasswordValidationSchema),
    // See Form.tsx's own comment on this same pattern: a Controller-bound field needs an
    // explicit "" default to match Yup's error selection against the pre-Carbon uncontrolled
    // `register()` input, whose native `value` already defaulted to "".
    defaultValues: { email: "" },
  });

  const {
    mutate: resetPassword,
    isPending,
    isSuccess,
    isError,
  } = useMutation({
    mutationFn: async (data: ResetPasswordFormValues) =>
      await axios.post("/auth/reset-password", data),
    onSuccess: () => reset(),
  });

  const onSubmit = (data: ResetPasswordFormValues) => {
    resetPassword(data);
  };

  return (
    <AuthLayout>
      {isSuccess ? (
        <InlineNotification
          kind="success"
          lowContrast
          hideCloseButton
          title={t("newPassword.inbox")}
          className="reset-password__notification"
        />
      ) : null}

      {isError ? (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={t("newMaterialForm.error")}
          className="reset-password__notification"
        />
      ) : null}

      {isPending ? (
        <Loading withOverlay={false} description={t("cta.loading")} />
      ) : (
        <>
          <h3 className="reset-password__title">{t("newPassword.reset")}</h3>
          <p className="reset-password__copy">{t("newPassword.revert")}</p>
          <CarbonForm
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            noValidate
            className="reset-password__form"
            autoComplete="off"
          >
            <TextField
              name="email"
              type="email"
              label={t("login.email")}
              control={control}
              required
            />

            <div className="reset-password__actions">
              <Link as={RouterLink} to="/login">
                {t("login.signIn")}
              </Link>
              <Button type="submit">{t("cta.send")}</Button>
            </div>
          </CarbonForm>
        </>
      )}
    </AuthLayout>
  );
};

export default ResetPassword;
